import React, { useState } from 'react';

interface FaqItem {
  q: string;
  a: React.ReactNode;
}

const FAQS: { category: string; items: FaqItem[] }[] = [
  {
    category: 'About Visa Hub',
    items: [
      {
        q: 'What is Visa Hub?',
        a: 'Visa Hub is a free travel tool that instantly checks visa requirements between any two countries. Enter your passport country and destination, and Visa Hub tells you whether you need a visa, can travel visa-free, qualify for a visa on arrival, or need to apply for an eVisa — along with the maximum stay duration, currency exchange rate, and a pre-travel checklist.',
      },
      {
        q: 'Is Visa Hub free to use?',
        a: 'Yes. Visa Hub is completely free. There are no subscriptions, sign-ups, or paywalls. All core features — visa checker, passport explorer, validity calculator, and entry checklists — are available at no cost.',
      },
      {
        q: 'Who is Visa Hub for?',
        a: 'Visa Hub is built for anyone planning international travel: tourists, digital nomads, business travelers, students studying abroad, and expats. It is especially useful for quickly confirming entry requirements before booking flights or accommodation.',
      },
    ],
  },
  {
    category: 'Using the App',
    items: [
      {
        q: 'How do I check visa requirements?',
        a: (
          <>
            <p>On the main screen, select your passport country from the first dropdown and your destination country from the second, then tap <strong>Check Requirements</strong>. Results appear within a few seconds and include:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Visa requirement type (visa-free, eVisa, visa on arrival, or visa required)</li>
              <li>Maximum permitted stay</li>
              <li>Currency exchange rate</li>
              <li>Pre-travel entry checklist</li>
              <li>Visa validity calculator (for visa-free destinations)</li>
            </ul>
          </>
        ),
      },
      {
        q: 'What is the Passport Explorer?',
        a: 'The Passport Explorer lets you see all destinations accessible with a given passport, grouped by requirement type. Select your passport to get a count of visa-free countries, eVisa destinations, visa-on-arrival countries, and countries that require a full visa — with a searchable, filterable list of all destinations.',
      },
      {
        q: 'What does the Visa Validity Calculator do?',
        a: 'When a destination allows visa-free entry for a specific number of days (for example, 90 days), you can enter your planned entry date and the calculator will show your exact required exit date and a countdown of days remaining.',
      },
      {
        q: 'What is the Pre-Travel Checklist?',
        a: 'Every result card includes a checklist of documents and requirements you should prepare before traveling — such as proof of onward travel, travel insurance, passport validity, accommodation bookings, and proof of funds. The checklist adjusts based on the visa type of your route.',
      },
      {
        q: 'Can I save routes I have checked before?',
        a: 'Yes. Your recent checks are saved automatically on your device and appear as quick-access pills below the search form. Tap any saved route to re-run the check instantly. You can remove individual entries or clear all history at any time.',
      },
      {
        q: 'Does Visa Hub work offline?',
        a: 'Visa Hub requires an internet connection to fetch live visa requirement data. Offline use is not currently supported, but recently checked routes are stored on your device and may display cached results when connectivity is limited.',
      },
    ],
  },
  {
    category: 'Data & Accuracy',
    items: [
      {
        q: 'How accurate is the visa information?',
        a: 'Visa Hub sources data from the Passport Index dataset, which is maintained by the travel research community and updated regularly. While we strive to keep information current, visa policies can change without notice. Always verify requirements with the official embassy or consulate of your destination country before traveling.',
      },
      {
        q: 'How often is the data updated?',
        a: 'The underlying visa dataset is refreshed regularly. Individual route results are cached for up to 24 hours to ensure fast performance; the global passport index is refreshed weekly. For time-sensitive travel, we recommend confirming with official sources regardless.',
      },
      {
        q: 'Can I rely on Visa Hub for my visa application?',
        a: 'Visa Hub is an informational tool only and does not constitute legal or immigration advice. It is intended to help you understand requirements at a glance and prepare your research. For official requirements, application forms, fees, and processing times, always consult the embassy, consulate, or official government immigration portal of your destination country.',
      },
      {
        q: 'Why does my result say "Check with embassy"?',
        a: "This means Visa Hub does not have confirmed data for that specific passport and destination combination. This can occur for less common routes or countries with frequently changing policies. In these cases, contact the destination country's embassy or consulate directly for accurate, up-to-date requirements.",
      },
    ],
  },
  {
    category: 'Privacy & Data',
    items: [
      {
        q: 'Does Visa Hub store my personal information?',
        a: 'Visa Hub does not require you to create an account. Your recent route history is stored locally on your device only and is never sent to our servers. We do collect anonymized usage analytics (such as which routes are checked most often) to improve the product.',
      },
      {
        q: 'Does Visa Hub sell my data?',
        a: 'No. We do not sell personal information. For full details on how we handle data, please read our Privacy Policy at visahub.info/privacy.',
      },
      {
        q: 'How do I delete my saved history?',
        a: 'Tap "Clear all" in the Recent Checks section on the main screen. This removes all locally stored route history from your device immediately.',
      },
    ],
  },
  {
    category: 'Technical & Support',
    items: [
      {
        q: 'Which platforms is Visa Hub available on?',
        a: 'Visa Hub is available as a web app at visahub.info and as a mobile app on the Apple App Store. It works on all modern browsers including Safari, Chrome, Firefox, and Edge.',
      },
      {
        q: 'The app returned an error. What should I do?',
        a: (
          <>
            <p>If you see an error when checking visa requirements, try the following:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Check your internet connection and try again.</li>
              <li>Wait a few seconds and tap Check Requirements again — occasional timeouts can occur.</li>
              <li>Make sure you have selected both a passport and destination country.</li>
            </ul>
            <p className="mt-2">If the problem persists, contact us at <a href="mailto:support@gaillimolabs.com" className="text-blue-600 hover:underline">support@gaillimolabs.com</a>.</p>
          </>
        ),
      },
      {
        q: 'How do I report incorrect visa information?',
        a: (
          <>
            We take data accuracy seriously. If you believe a visa requirement shown in the app is incorrect, please email us at{' '}
            <a href="mailto:support@gaillimolabs.com" className="text-blue-600 hover:underline">
              support@gaillimolabs.com
            </a>{' '}
            with the passport country, destination country, and the correct information along with a source link if available. We will review and update it promptly.
          </>
        ),
      },
      {
        q: 'How do I contact support?',
        a: (
          <>
            You can reach us at{' '}
            <a href="mailto:support@gaillimolabs.com" className="text-blue-600 hover:underline">
              support@gaillimolabs.com
            </a>
            . We aim to respond within 2 business days.
          </>
        ),
      },
    ],
  },
];

function AccordionItem({ q, a }: FaqItem) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border-b border-slate-100 last:border-0 ${open ? 'bg-slate-50/50' : ''}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full text-left px-6 py-4 flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors"
      >
        <span className="text-sm font-semibold text-slate-800">{q}</span>
        <svg
          className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="px-6 pb-5 text-sm text-slate-600 leading-relaxed">
          {a}
        </div>
      )}
    </div>
  );
}

export default function Faq() {
  return (
    <div className="min-h-screen bg-slate-50">

      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-blue-600 rounded-md flex items-center justify-center shadow-sm">
              <span className="text-white font-black text-xs italic">V</span>
            </div>
            <span className="font-bold text-slate-900 text-[15px]">Visa Hub</span>
          </a>
          <a href="/" className="text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors">
            ← Back to app
          </a>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <header className="mb-10">
          <h1 className="text-3xl font-black text-slate-900 mb-2">Frequently Asked Questions</h1>
          <p className="text-sm text-slate-400">
            Everything you need to know about Visa Hub.{' '}
            <a href="mailto:support@gaillimolabs.com" className="text-blue-600 hover:underline">
              Can't find an answer? Contact us.
            </a>
          </p>
        </header>

        <div className="space-y-6">
          {FAQS.map(group => (
            <section key={group.category}>
              <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 px-1">
                {group.category}
              </h2>
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                {group.items.map(item => (
                  <AccordionItem key={item.q} {...item} />
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-10 bg-blue-50 border border-blue-100 rounded-2xl px-8 py-7 text-center">
          <p className="text-sm font-semibold text-slate-800 mb-1">Still have questions?</p>
          <p className="text-xs text-slate-500 mb-4">Our support team is happy to help.</p>
          <a
            href="mailto:support@gaillimolabs.com"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors"
          >
            Contact Support
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8 px-6 mt-4">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <span>© 2026 Gaillimo Labs. All rights reserved.</span>
          <div className="flex items-center gap-4">
            <a href="/privacy" className="hover:text-slate-600 transition-colors">Privacy Policy</a>
            <a href="/" className="hover:text-slate-600 transition-colors">← Back to Visa Hub</a>
          </div>
        </div>
      </footer>

    </div>
  );
}
